import React, { useState } from "react";
import "./App.css";
import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import {
  Trees, ArrowLeft, MapPin, Calendar, TrendingUp, Droplets,
  ThermometerSun, Wind, Camera, Activity, CheckCircle, AlertTriangle,
  Download, Share2
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, Radar
} from "recharts";

// Hooks & Components
import { usePlotDetail } from "./hooks/usePlotDetail";
import LoadingSpinner from "./components/LoadingSpinner";

/** ==== Components ==== */
const MetricCard = ({ icon: Icon, title, value, unit, status }) => (
  <div className="card">
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
      <Icon size={18} color="#10b981" />
      <div className="card-title" style={{ fontSize: ".9rem" }}>{title}</div>
    </div>
    <div style={{ fontSize: "1.8rem", fontWeight: 700, color: "var(--text-primary)" }}>
      {value}
      <span style={{ fontSize: "1rem", fontWeight: 400, color: "var(--text-muted)", marginLeft: 6 }}>
        {unit}
      </span>
    </div>
  </div>
);

// Placeholders for charts until real history data is available in subcollections
const growthData = [
  { month: "Jan", height: 12.2, biomass: 45 },
  { month: "Feb", height: 12.8, biomass: 48 },
  { month: "Mar", height: 13.5, biomass: 52 },
  { month: "Apr", height: 14.3, biomass: 57 },
  { month: "May", height: 15.1, biomass: 63 },
  { month: "Jun", height: 15.9, biomass: 68 },
];

const GrowthChart = () => (
  <div className="card">
    <div className="card-header">
      <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <TrendingUp size={16} /> Growth Trend
      </div>
      <span className="badge">Last 6 months</span>
    </div>
    <div className="chart-lg">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={growthData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
          <XAxis dataKey="month" tick={{ fill: "#475569" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
          <YAxis yAxisId="left" tick={{ fill: "#475569" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
          <YAxis yAxisId="right" orientation="right" tick={{ fill: "#475569" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
          <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#1e293b" }} />
          <Line yAxisId="left" type="monotone" dataKey="height" stroke="#10b981" strokeWidth={2} name="Height (m)" />
          <Line yAxisId="right" type="monotone" dataKey="biomass" stroke="#6ee7b7" strokeWidth={2} name="Biomass (kg)" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

export default function PlotDetail() {
  const { plotId } = useParams();
  const navigate = useNavigate();
  
  const { plot, loading, error } = usePlotDetail(plotId);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="container">Error loading plot details</div>;
  if (!plot) return <div className="container">Plot not found</div>;

  return (
    <div className="app-container">
      {/* Navbar */}
      <div className="navbar">
        <div 
          className="logo" 
          style={{ height: '60px', overflow: 'hidden', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <img 
            src="/Lateral Original.png" 
            alt="Sango" 
            style={{ 
              height: '110px', 
              width: 'auto',
              objectFit: 'contain',
              objectPosition: 'center'
            }}
          />
        </div>
        <div className="nav-right">
          <button className="btn btn-ghost"><Share2 size={16}/> Share</button>
          <button className="btn btn-outline"><Download size={16}/> Report</button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container" style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
        {/* Back Button */}
        <motion.button 
          whileHover={{ x: -4 }}
          className="btn btn-ghost" 
          style={{ marginBottom: 20, padding: "8px 12px" }}
          onClick={() => navigate('/')}
        >
          <ArrowLeft size={18} /> Back to Portfolio
        </motion.button>

        {/* Header Section */}
        <div className="card" style={{ padding: "24px", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 20 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                <Trees size={32} color="#10b981" />
                <div>
                  <h1 style={{ fontSize: "2rem", fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>
                    {plot.name || `Plot ${plotId}`}
                  </h1>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 6, fontSize: ".95rem", color: "var(--text-muted)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <MapPin size={14} /> {plot.forestId} (Lat: {plot.center?._lat}, Long: {plot.center?._long})
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <Calendar size={14} /> Planted: {plot.createdAt ? new Date(plot.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
                <span className="pill" style={{ background: "rgba(16, 185, 129, 0.1)", color: "#047857", fontSize: ".9rem", padding: "6px 14px" }}>
                  Health Score: {plot.health?.score || 'N/A'}
                </span>
                <span className="badge">{plot.treesPlanted} / {plot.capacity} Trees</span>
                <span className="badge">{plot.species}</span>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: ".9rem", color: "var(--text-muted)", marginBottom: 4 }}>Status</div>
              <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--text-primary)", textTransform: 'capitalize' }}>{plot.status}</div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid-4" style={{ marginBottom: 24 }}>
          <MetricCard 
            icon={TrendingUp} 
            title="Avg Height" 
            value={plot.health?.averageTreeHeight || 0} 
            unit="m" 
            status="up"
          />
          <MetricCard 
            icon={Droplets} 
            title="Soil Moisture" 
            value={plot.health?.soil?.moisture || 0} 
            unit="%" 
            status={plot.health?.soil?.moisture < 30 ? "down" : "up"}
          />
          <MetricCard 
            icon={ThermometerSun} 
            title="Temperature" 
            value={plot.health?.soil?.temperature || 0} 
            unit="°C" 
            status="up"
          />
          <MetricCard 
            icon={Wind} 
            title="Biomass" 
            value={plot.health?.biomassDensity || 0} 
            unit="kg/m²" 
            status="up"
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid" style={{ gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 24 }}>
          <GrowthChart />
          <div className="card">
            <div className="card-title">Latest Imagery</div>
            {plot.media?.latestDroneImageUrl ? (
              <img src={plot.media.latestDroneImageUrl} alt="Drone View" style={{ width: '100%', borderRadius: 8, marginTop: 10 }} />
            ) : (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>No imagery available</div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="footer">© {new Date().getFullYear()} Sango • Invest in African Living Assets</div>
    </div>
  );
}

